/**
 * API Key Rotate Dialog Component
 *
 * Confirmation dialog for rotating an API key.
 * Generates a new key and shows it to the user (copy-to-clipboard).
 */

import { useState } from 'react';
import { RefreshCw, Loader2, Copy, Check, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useRotateApiKey } from '@/hooks/use-api-keys';
import { useToast } from '@/hooks/use-toast';
import type { ApiKey, RotateApiKeyResponse } from '@/types/api-key';

interface ApiKeyRotateDialogProps {
  apiKey: ApiKey | null;
  onClose: () => void;
}

/**
 * API Key Rotate Dialog
 * Renders confirmation dialog for rotating an API key
 */
export function ApiKeyRotateDialog({ apiKey, onClose }: ApiKeyRotateDialogProps) {
  const { toast } = useToast();
  const [isRotating, setIsRotating] = useState(false);
  const [rotationResult, setRotationResult] = useState<RotateApiKeyResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const rotateApiKeyMutation = useRotateApiKey();

  const handleRotate = async () => {
    if (!apiKey) return;

    setIsRotating(true);
    try {
      const result = await rotateApiKeyMutation.mutateAsync(apiKey.id);
      setRotationResult(result);

      toast({
        title: 'API Key Rotated',
        description: `${apiKey.keyName} has been rotated successfully`,
      });
    } catch (error) {
      toast({
        title: 'Rotation Failed',
        description: error instanceof Error ? error.message : 'Failed to rotate API key',
        variant: 'destructive',
      });
    } finally {
      setIsRotating(false);
    }
  };

  const handleCopyKey = async () => {
    if (!rotationResult?.data.newApiKey) return;

    try {
      await navigator.clipboard.writeText(rotationResult.data.newApiKey);
      setCopied(true);
      toast({
        title: 'Copied!',
        description: 'API key copied to clipboard',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: 'Copy Failed',
        description: 'Failed to copy to clipboard',
        variant: 'destructive',
      });
    }
  };

  const handleClose = () => {
    setRotationResult(null);
    setCopied(false);
    onClose();
  };

  // If we have a rotation result, show the new key
  if (rotationResult) {
    return (
      <Dialog open={true} onOpenChange={handleClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Check className="h-5 w-5" />
              API Key Rotated Successfully
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Save this key now!</AlertTitle>
              <AlertDescription>
                This is the only time you will see this key. Copy it and store it securely.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <label className="text-sm font-medium">New API Key</label>
              <div className="flex gap-2">
                <code className="flex-1 p-3 bg-muted rounded-md text-sm font-mono break-all">
                  {rotationResult.data.newApiKey}
                </code>
                <Button variant="outline" size="icon" onClick={handleCopyKey}>
                  {copied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="bg-muted p-4 rounded-md space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Key Name:</span>
                <span className="font-medium">{rotationResult.data.newKeyName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Client Type:</span>
                <span className="font-medium">{rotationResult.data.clientType.toUpperCase()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Rotated By:</span>
                <span className="font-medium">{rotationResult.data.rotatedBy}</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={handleClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={apiKey !== null} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Rotate API Key
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to rotate this API key? This will generate a new key and the old
            one will no longer work.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Warning</AlertTitle>
            <AlertDescription>
              After rotation, you must update all applications using this key immediately. The old
              key will be deactivated.
            </AlertDescription>
          </Alert>

          <div className="bg-muted p-4 rounded-md space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Key Name:</span>
              <span className="font-medium">{apiKey?.keyName}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Client Type:</span>
              <span className="font-medium">{apiKey?.clientType.toUpperCase()}</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isRotating}>
            Cancel
          </Button>
          <Button onClick={handleRotate} disabled={isRotating}>
            {isRotating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Rotating...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Rotate Key
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
